// Shared timeline event list — used by the client Timeline tab and the deal dialog.
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Building2, CalendarClock, FileText, Briefcase, Workflow, Hammer, PencilRuler, CheckSquare, StickyNote, Paperclip, Trash2 } from "lucide-react";

export const TIMELINE_STYLE = {
  client: { icon: Building2, color: "bg-slate-600" },
  booking: { icon: CalendarClock, color: "bg-violet-600" },
  quote: { icon: FileText, color: "bg-cyan-600" },
  job: { icon: Briefcase, color: "bg-emerald-600" },
  deal: { icon: Workflow, color: "bg-amber-500" },
  fabrication: { icon: Hammer, color: "bg-orange-600" },
  design: { icon: PencilRuler, color: "bg-violet-600" },
  activity: { icon: CheckSquare, color: "bg-sky-500" },
  note: { icon: StickyNote, color: "bg-teal-600" },
  file: { icon: Paperclip, color: "bg-slate-700" },
};

const viewFile = async (id) => {
  try {
    const res = await api.get(`/timeline/files/${id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch {
    toast.error("Could not open file");
  }
};

export const TimelineEvents = ({ events, navigate, onChanged, readOnly = false }) => {
  const linkFor = (e) => e.kind === "quote" ? `/quotes/${String(e.id).replace(/-(trail-\d+|sent|accepted|declined)$/, "")}` : e.kind === "job" ? `/jobs/${e.id}` : e.kind === "booking" ? "/scheduler" : e.kind === "deal" ? "/deals" : e.kind === "fabrication" ? "/fabrication" : e.kind === "design" ? "/blueprints" : null;
  const remove = async (e) => {
    if (!window.confirm("Remove this from the timeline?")) return;
    try {
      await api.delete(e.kind === "note" ? `/timeline/notes/${e.id}` : `/timeline/files/${e.id}`);
      toast.success("Removed");
      onChanged?.();
    } catch {
      toast.error("Could not remove");
    }
  };
  return (
    <div className="relative pl-8 space-y-5">
      <div className="absolute left-[11px] top-1 bottom-1 w-px bg-slate-200" />
      {events.map((e, i) => {
        const s = TIMELINE_STYLE[e.kind] || TIMELINE_STYLE.activity;
        const Icon = s.icon;
        const link = e.kind === "file" ? null : linkFor(e);
        const clickable = e.kind === "file" || link;
        return (
          <div key={`${e.kind}-${e.id}-${i}`} className="relative" data-testid={`timeline-event-${e.kind}-${e.id}`}>
            <div className={`absolute -left-8 w-6 h-6 rounded-full ${s.color} text-white flex items-center justify-center shadow-sm`}>
              <Icon size={12} />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className={`text-sm font-medium text-slate-900 ${clickable ? "cursor-pointer hover:text-cyan-700 hover:underline" : ""}`}
                  onClick={() => { if (e.kind === "file") viewFile(e.id); else if (link) navigate?.(link); }}
                  data-testid={e.kind === "file" ? `timeline-file-open-${e.id}` : undefined}
                >
                  {e.title}
                </span>
                {e.status && <Badge variant="outline" className="rounded-sm text-[9px] uppercase">{e.status}</Badge>}
                {!readOnly && (e.kind === "note" || e.kind === "file") && (
                  <button type="button" onClick={() => remove(e)} title="Remove"
                    className="text-slate-300 hover:text-rose-600 transition-colors"
                    data-testid={`timeline-delete-${e.kind}-${e.id}`}>
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
              {e.detail && <div className={`text-xs text-slate-500 mt-0.5 ${e.kind === "note" ? "whitespace-pre-wrap" : ""}`}>{e.detail}</div>}
              <div className="text-[11px] text-slate-400 font-mono mt-0.5">
                {e.date ? new Date(e.date).toLocaleString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" }) : ""}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
